import {
  Box,
  Button,
  Center,
  Checkbox,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Link,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Stack,
  Text,
  useColorModeValue,
  useDisclosure,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { AsyncSelect } from 'chakra-react-select';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import startCase from 'lodash-es/startCase.js';
import { useEffect, useRef, useState, useMemo } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { Logo } from '@duke-hq/ui/components/Logo/Logo';
import { LogoFooter } from '@duke-hq/ui/components/LogoFooter/LogoFooter';
import { trpcTenant } from './tenant/trpcTenant';
import { djs } from '@duke-hq/djs';
import { useAuthTenant } from './tenant/useAuthTenant';
import { useDebounceCallback } from 'usehooks-ts';

interface SignUpFormProps {
  email: string;
  first_name: string;
  last_name: string;
  isAgreed: boolean;
  site: string | null;
}

export function SelfSignup() {
  const meQuery = trpcTenant.me.useQuery();
  const { user } = useAuthTenant();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();
  const formRef = useRef<HTMLFormElement>(null);
  const [searchParams] = useSearchParams();
  const [isButtonLoading, setIsButtonLoading] = useState<boolean>(false);

  const [email, setEmail] = useState<string>('');
  const [emailSentAt, setEmailSentAt] = useState<Date | null>(null);

  const location = useLocation();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const defaultFormState: SignUpFormProps = {
    email: searchParams.get('email') ?? '',
    first_name: searchParams.get('firstname') ?? '',
    last_name: searchParams.get('lastname') ?? '',
    isAgreed: false,
    site: null,
  };

  const {
    handleSubmit,
    register,
    control,
    clearErrors,
    formState: { errors, isValid },
  } = useForm<{
    email: string;
    first_name: string;
    last_name: string;
    site: string | null;
    isAgreed: boolean;
  }>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      ...defaultFormState,
    },
  });

  const { data: sitesList, isPending: isSitesPending } =
    trpcTenant.auth.listSiteLocations.useQuery();

  const createConsultant = trpcTenant.auth.consultantSelfSignup.useMutation({
    onSuccess() {
      setIsButtonLoading(false);
      setEmailSentAt(new Date());
      onOpen();
    },
    onError: (error) => {
      setIsButtonLoading(false);
      toast({
        title: t`Failed to create your account.`,
        description: error.message
          ? error.message
          : t`Something went wrong. Please try again, or contact support for help.`,
        status: 'error',
        isClosable: true,
        duration: 7000,
      });
    },
  });

  const magicLinkLogin = trpcTenant.auth.magicLinkLogin.useMutation();

  const sitesOptions = useMemo(() => {
    return (
      sitesList?.map((site) => ({
        label: `${site.name}, ${site.state}`,
        value: site.id,
      })) ?? []
    );
  }, [sitesList]);

  const loadSitesOptions = useDebounceCallback((inputValue: string, callback: any) => {
    if (sitesOptions) {
      callback(
        sitesOptions.filter(
          (option) =>
            option.label.toLowerCase().includes(inputValue.toLowerCase()) ||
            option.value.toLowerCase().includes(inputValue.toLowerCase()),
        ),
      );
    } else {
      callback([]);
    }
  }, 500);

  const onSubmit = async (data) => {
    const { email, first_name, last_name, site, isAgreed } = data;
    const apiPayload = {
      email,
      first_name,
      last_name,
      site,
      isAgreed,
    };

    setEmail(email);
    setIsButtonLoading(true);
    await createConsultant.mutateAsync(apiPayload);
  };

  useEffect(() => {
    if (user) {
      navigate('/');
    }
  }, [navigate, user]);

  useEffect(() => {
    const handleMagicLinkLogin = async (token: string) => {
      await magicLinkLogin.mutateAsync({
        token,
      });
      await meQuery.refetch();
    };
    if (location.pathname === '/magiclink') {
      if (token) {
        handleMagicLinkLogin(token);
        navigate('/');
      }
    }
  }, [location.pathname, magicLinkLogin, meQuery, navigate, token]);

  return (
    <Flex
      minH="100vh"
      align="center"
      justify="center"
      bg={useColorModeValue('bg.muted', 'gray.800')}
      p="32px"
    >
      <Stack mx="auto" width="auto" maxW="530px" gap="32px" align="center">
        <Logo />

        <Stack
          align="center"
          width="full"
          px="64px"
          py="32px"
          gap="32px"
          bg="bg.surface"
          borderColor="border.emphasis"
          borderRadius="12px"
        >
          <Text
            fontSize="3xl"
            lineHeight="10"
            fontWeight="semibold"
            color="fg.default"
            align="center"
          >
            <Trans>Join the learning revolution</Trans>
          </Text>

          <Text
            fontSize="xl"
            lineHeight="7"
            fontWeight="semibold"
            color="fg.default"
            align="center"
          >
            <Trans>Ready to start?</Trans>
            <br />
            <Trans>Create an account for</Trans>&nbsp;
            {startCase(window.location.hostname.split('.')[0])}
          </Text>

          <form
            onSubmit={handleSubmit(onSubmit)}
            style={{ width: '100%' }}
            ref={formRef}
            noValidate
          >
            <Stack gap="16px" p={4}>
              <FormControl isInvalid={!!errors.email} isRequired>
                <FormLabel htmlFor="email">
                  <Trans>Email</Trans>
                </FormLabel>
                <Input
                  autoFocus
                  type="email"
                  autoComplete="off"
                  {...register('email', {
                    required: t`Email is required`,
                    pattern: {
                      value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                      message: t`Please enter a valid email address`,
                    },
                    onChange: () => {
                      if (errors.email) {
                        clearErrors('email');
                      }
                    },
                  })}
                />
                <FormErrorMessage>{errors.email?.message}</FormErrorMessage>
              </FormControl>

              <FormControl isInvalid={!!errors.first_name} isRequired>
                <FormLabel htmlFor="first_name">
                  <Trans>First name</Trans>
                </FormLabel>
                <Input
                  autoComplete="off"
                  {...register('first_name', {
                    required: t`First name is required`,
                    onChange: () => {
                      if (errors.first_name) {
                        clearErrors('first_name');
                      }
                    },
                  })}
                />
                <FormErrorMessage>{errors.first_name?.message}</FormErrorMessage>
              </FormControl>

              <FormControl isInvalid={!!errors.last_name} isRequired>
                <FormLabel htmlFor="last_name">
                  <Trans>Last name</Trans>
                </FormLabel>
                <Input
                  autoComplete="off"
                  {...register('last_name', {
                    required: t`Last name is required`,
                    onChange: () => {
                      if (errors.last_name) {
                        clearErrors('last_name');
                      }
                    },
                  })}
                />
                <FormErrorMessage>{errors.last_name?.message}</FormErrorMessage>
              </FormControl>

              {isSitesPending ? (
                <Center p={2}>
                  <Spinner size="sm" />
                </Center>
              ) : sitesOptions.length === 0 ? (
                <Center p={8}>
                  <Text textAlign="center">
                    <Trans>No sites found</Trans>
                  </Text>
                </Center>
              ) : (
                <FormControl isInvalid={!!errors.site} isRequired>
                  <FormLabel htmlFor="site">
                    <Trans>Site</Trans>
                  </FormLabel>
                  <Controller
                    name="site"
                    control={control}
                    render={({ field }) => (
                      <AsyncSelect
                        id="site"
                        name="site"
                        placeholder={t`Select site`}
                        defaultOptions={sitesOptions}
                        loadOptions={loadSitesOptions}
                        selectedOptionColorScheme={'brand'}
                        onChange={(val) => field.onChange(val?.value)}
                        isLoading={isSitesPending}
                        isDisabled={sitesOptions.length === 0}
                      />
                    )}
                    rules={{
                      required: t`Please select a site`,
                    }}
                  />
                  <FormErrorMessage>{errors.site?.message}</FormErrorMessage>
                </FormControl>
              )}

              <FormControl id="isAgreed" isInvalid={!!errors.isAgreed} isRequired>
                <Flex align="start" gap="8px">
                  <Controller
                    name="isAgreed"
                    control={control}
                    render={({ field }) => (
                      <Checkbox
                        {...field}
                        isChecked={field.value}
                        alignItems="baseline"
                        p="2px"
                        value={field.value.toString()}
                        onChange={(e) => {
                          field.onChange(e.target.checked);
                          clearErrors('isAgreed');
                        }}
                      />
                    )}
                    rules={{
                      required: t`You must agree to the terms and conditions.`,
                    }}
                  />
                  <Text fontSize="xs" lineHeight="4">
                    <Trans>You agree to the</Trans>&nbsp;
                    <Link
                      href="https://drive.google.com/file/d/1uwuClmrRcpJ9lOtKf7MH4otgG-UjXMRS/view?usp=sharing"
                      isExternal
                    >
                      <Trans>Duke Terms of Service</Trans>
                    </Link>
                    &nbsp;
                    <Trans>and</Trans>&nbsp;
                    <Link href="https://duke.co/privacy-policy" isExternal>
                      <Trans>Privacy Statement</Trans>
                    </Link>
                    .<br />
                    <Trans>We'll occasionally send you account-related emails.</Trans>
                  </Text>
                </Flex>
                <FormErrorMessage>{errors.isAgreed?.message}</FormErrorMessage>
              </FormControl>

              <Button
                type="submit"
                isLoading={isButtonLoading}
                loadingText="Submitting"
                isDisabled={!isValid}
              >
                <Trans>Sign up</Trans>
              </Button>

              <Flex
                flexDirection={{ base: 'column', md: 'row' }}
                gap={{ base: '4px', md: '8px' }}
                justify="center"
                align="center"
              >
                <Text fontSize="md" lineHeight="6" fontWeight="medium">
                  <Trans>Already have an account?</Trans>
                </Text>
                <Link href="/login">Log in</Link>
              </Flex>
            </Stack>
          </form>

          <Flex flexDirection="column" justify="center" align="center">
            <Text fontSize="sm" lineHeight="5" color="fg.muted" align="center">
              <Trans>Having trouble logging in?</Trans>
              <br />
              <Trans>Email</Trans>&nbsp;
              <Link href="mailto:support@duke.co">support@duke.co</Link>&nbsp;
              <Trans>or call 1300 Duke Co</Trans>
            </Text>
          </Flex>
        </Stack>

        <LogoFooter />
      </Stack>

      <Modal closeOnOverlayClick={false} isOpen={isOpen} onClose={onClose} isCentered size="full">
        <ModalOverlay />

        <ModalContent bg="bg.muted">
          <ModalHeader></ModalHeader>
          <ModalCloseButton />
          <ModalBody as={Flex} align="center" justify="center">
            <Stack
              flexDirection="column"
              mx="auto"
              height="full"
              width="auto"
              maxW="530px"
              gap="32px"
              align="center"
            >
              <Logo />
              <Stack
                align="center"
                width="full"
                px="64px"
                py="32px"
                gap="32px"
                bg="bg.surface"
                borderColor="border.emphasis"
                borderRadius="12px"
              >
                <Text
                  fontSize="2xl"
                  lineHeight="8"
                  fontWeight="semibold"
                  color="fg.default"
                  align="center"
                >
                  <Trans>Check your inbox for your</Trans>
                  <br />
                  <Trans>signup link</Trans>
                </Text>

                <VStack width="full">
                  <Text fontSize="md" lineHeight="6" color="fg.muted" align="center">
                    <Trans>We've sent a signup link to</Trans>&nbsp;
                    <Box as="span" fontWeight="semibold">
                      {email}
                    </Box>
                  </Text>

                  {createConsultant.data ? (
                    <>
                      <Text fontSize="sm" color="fg.muted" align="center">
                        <Trans>Sent {djs(emailSentAt).fromNow()}</Trans>
                      </Text>
                      <Flex
                        flexDirection={{ base: 'column', md: 'row' }}
                        gap={{ base: '4px', md: '8px' }}
                        justify="center"
                        align="center"
                        mt={4}
                      >
                        <Text fontSize="md" lineHeight="6" fontWeight="medium">
                          <Trans>Didn't receive an email?</Trans>{' '}
                        </Text>
                        <Link href="/login">Back home</Link>
                      </Flex>
                    </>
                  ) : (
                    <Flex flexDirection="column" justify="center" align="center" gap={0}>
                      <Text fontSize="md" fontWeight="medium" align="center">
                        <Trans>We couldn't complete your sign-up.</Trans>
                      </Text>
                      <Text fontSize="sm" align="center">
                        <Trans>Please check your details and try again.</Trans>
                      </Text>
                      <Link onClick={onClose} mt={4}>
                        Back
                      </Link>
                      <Text fontSize="sm" lineHeight="5" color="fg.muted" align="center" mt={4}>
                        <Trans>If the issue persists, our support team is here to help!</Trans>
                        <br />
                        <Trans>Email</Trans>&nbsp;
                        <Link href="mailto:support@duke.co">support@duke.co</Link>
                        &nbsp;
                        <Trans>or call</Trans>&nbsp;
                        <Link href="tel:1300.Duke.Co">1300 Duke Co</Link>
                      </Text>
                    </Flex>
                  )}
                </VStack>
              </Stack>
            </Stack>
          </ModalBody>
        </ModalContent>
      </Modal>
    </Flex>
  );
}
